import React from 'react';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import {
  RANKING_EVAL_TYPES,
  type EvaluatorScoreMetrics,
  type RankingEvalTypeId,
  getContentTypeLabel,
} from '@utils/rankingEvaluationNav';
import { getEvaluatorTheme } from '@utils/rankingEvaluatorTheme';

const emptyMetrics = (): EvaluatorScoreMetrics => ({
  totalScore: 0,
  maxScore: 0,
  ratedCount: 0,
  notRatedCount: 0,
});

const PanelScoreBar: React.FC<{
  metrics: EvaluatorScoreMetrics;
}> = ({ metrics }) => {
  const pct = metrics.maxScore > 0 ? Math.round((metrics.totalScore / metrics.maxScore) * 100) : 0;
  const hasData = metrics.maxScore > 0 || metrics.ratedCount > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
        borderRadius: 999,
        border: '1px solid rgba(21, 101, 192, 0.28)',
        boxShadow: '0 4px 12px rgba(21, 101, 192, 0.12)',
        minHeight: 34,
        flexShrink: 0,
        bgcolor: '#fff',
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.45,
          minWidth: { xs: 108, sm: 136 },
          background: 'linear-gradient(135deg, #1565c0 0%, #42a5f5 100%)',
          color: '#fff',
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, lineHeight: 1, fontSize: '0.62rem' }}>
          Total Score
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2, fontSize: '0.78rem' }}>
          {hasData ? `${metrics.totalScore}/${metrics.maxScore} (${pct}%)` : '—'}
        </Typography>
      </Box>
      <Box
        sx={{
          px: 1,
          py: 0.45,
          minWidth: { xs: 48, sm: 58 },
          bgcolor: '#2e7d32',
          color: '#fff',
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, lineHeight: 1, fontSize: '0.62rem' }}>
          Rated
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2, fontSize: '0.78rem' }}>
          {hasData ? metrics.ratedCount : '—'}
        </Typography>
      </Box>
      <Box
        sx={{
          px: 1,
          py: 0.45,
          minWidth: { xs: 58, sm: 68 },
          bgcolor: '#fff3cd',
          color: '#7a4f01',
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, lineHeight: 1, fontSize: '0.62rem' }}>
          Not Rated
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 900, lineHeight: 1.2, fontSize: '0.78rem' }}>
          {hasData ? metrics.notRatedCount : '—'}
        </Typography>
      </Box>
    </Box>
  );
};

interface EvaluatorTabStripProps {
  sport: string;
  levelLabel: string;
  subcategory: string;
  activeEval: RankingEvalTypeId;
  onEvalChange: (evalId: RankingEvalTypeId) => void;
  onSave?: () => void;
  onDownloadManual?: () => void;
  onDownloadReport?: () => void;
  isSaving?: boolean;
  canSave?: boolean;
  canDownloadManual?: boolean;
  canDownloadReport?: boolean;
  metrics?: EvaluatorScoreMetrics;
  children: React.ReactNode;
}

const EvaluatorTabStrip: React.FC<EvaluatorTabStripProps> = ({
  sport,
  levelLabel,
  subcategory,
  activeEval,
  onEvalChange,
  onDownloadManual,
  onDownloadReport,
  isSaving = false,
  ratingsDirty = false,
  canRate = false,
  canDownloadManual = true,
  canDownloadReport = false,
  metrics,
  children,
}) => {
  const activeTheme = getEvaluatorTheme(activeEval);
  const contentLabel = getContentTypeLabel(sport, subcategory);
  const scoreMetrics = metrics || emptyMetrics();

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
        {contentLabel} · {sport} · {levelLabel}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 0.35,
          px: 0.5,
          pt: 0.5,
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          overflowX: { xs: 'auto', md: 'visible' },
          pb: 0.5,
        }}
      >
        {RANKING_EVAL_TYPES.map((evalType) => {
          const isActive = activeEval === evalType.id;
          const theme = getEvaluatorTheme(evalType.id);

          return (
            <Box
              key={evalType.id}
              onClick={() => onEvalChange(evalType.id)}
              role="tab"
              aria-selected={isActive}
              sx={{
                flex: { xs: '0 0 auto', md: '1 1 0' },
                minWidth: { xs: 120, md: 140 },
                cursor: 'pointer',
                position: 'relative',
                borderRadius: '14px 14px 0 0',
                background: isActive ? theme.gradient : theme.gradientInactive,
                color: isActive ? theme.textOnTab : theme.textInactive,
                pt: isActive ? 1.25 : 0.9,
                pb: isActive ? 1 : 0.75,
                px: 1.5,
                transform: isActive ? 'translateY(0)' : 'translateY(3px)',
                zIndex: isActive ? 3 : 1,
                boxShadow: isActive
                  ? '0 -2px 12px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.35)'
                  : `0 1px 4px ${theme.panelBorder}22, inset 0 1px 0 rgba(255,255,255,0.55)`,
                border: isActive ? `2px solid ${theme.panelBorder}` : `2px solid ${theme.panelBorder}55`,
                borderBottom: 'none',
                transition: 'all 0.18s ease',
                '&:hover': {
                  transform: isActive ? 'translateY(0)' : 'translateY(2px)',
                  boxShadow: isActive
                    ? '0 -2px 12px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.35)'
                    : `0 2px 8px ${theme.panelBorder}33, inset 0 1px 0 rgba(255,255,255,0.65)`,
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '2px 2px auto 2px',
                  height: '42%',
                  borderRadius: '10px 10px 0 0',
                  background: theme.glossHighlight,
                  pointerEvents: 'none',
                },
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.2,
                  fontSize: isActive ? '0.9rem' : '0.86rem',
                  position: 'relative',
                  whiteSpace: 'nowrap',
                  color: 'inherit',
                }}
              >
                {evalType.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          border: `2px solid ${activeTheme.panelBorder}`,
          borderRadius: '0 14px 14px 14px',
          background: activeTheme.panelBg,
          boxShadow: `0 10px 28px ${activeTheme.panelBorder}22`,
          overflow: 'hidden',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={1}
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: `1px solid ${activeTheme.panelBorder}33`,
            bgcolor: 'rgba(255,255,255,0.85)',
            gap: 1,
          }}
        >
          <PanelScoreBar metrics={scoreMetrics} />

          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75} sx={{ flexShrink: 0 }}>
            {canRate && (isSaving || ratingsDirty) && (
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mr: 0.5 }}>
                {isSaving && <CircularProgress size={14} sx={{ color: activeTheme.accent }} />}
                <Typography variant="caption" sx={{ fontWeight: 700, color: activeTheme.accent, whiteSpace: 'nowrap' }}>
                  {isSaving ? 'Auto-saving…' : 'Saving changes…'}
                </Typography>
              </Stack>
            )}
            <Tooltip title={canDownloadManual ? 'Download manual form' : 'Manual form not available'}>
              <span>
                <IconButton
                  size="small"
                  onClick={onDownloadManual}
                  disabled={!canDownloadManual}
                  sx={{ color: activeTheme.accent, bgcolor: '#fff', border: `1px solid ${activeTheme.accent}44` }}
                >
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canDownloadReport ? 'Download report PDF' : 'Submit evaluation to download report'}>
              <span>
                <IconButton
                  size="small"
                  onClick={onDownloadReport}
                  disabled={!canDownloadReport}
                  sx={{ color: '#c62828', bgcolor: '#fff', border: '1px solid rgba(198,40,40,0.35)' }}
                >
                  <PdfIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default EvaluatorTabStrip;
